import type { GuidePlacement } from '../engine/types';

/** Gap between the anchor and the popover, passed to Radix as `sideOffset`. */
export const POPOVER_SIDE_OFFSET = 4;
/** Minimum distance to the viewport edge, passed to Radix as `collisionPadding`. */
export const POPOVER_COLLISION_PADDING = 12;
/** Room the popover needs besides its own size: the gap plus the padding. */
const CLEARANCE = POPOVER_SIDE_OFFSET + POPOVER_COLLISION_PADDING;

const OPPOSITE: Record<GuidePlacement, GuidePlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

export interface Size {
  width: number;
  height: number;
}

interface PopoverPlacement {
  anchor: DOMRect;
  side: GuidePlacement;
}

function fits(side: GuidePlacement, rect: DOMRect, popover: Size) {
  const room = {
    top: rect.top,
    bottom: window.innerHeight - rect.bottom,
    left: rect.left,
    right: window.innerWidth - rect.right,
  }[side];
  const needed =
    side === 'top' || side === 'bottom' ? popover.height : popover.width;
  return room >= needed + CLEARANCE;
}

/**
 * Picks where the step popover goes so it always stays on screen.
 *
 * Radix's own collision handling only flips to the opposite side. When neither
 * side has room it keeps the requested one and lets the popover spill off the
 * viewport, because it never shifts the popover across its anchor. That is the
 * normal case for a section that fills the screen (a tournament list) or for a
 * field as wide as a phone, so the tour seemed to show nothing.
 *
 * When no side has room the popover has to cover part of the anchor, so it
 * goes against the edge of the screen where it covers the least: the bottom,
 * unless the anchor sits low enough that the top hides less of it (a field at
 * the end of a short page, which cannot scroll any higher). An anchor that
 * reaches the bottom too (a list filling the screen) is covered the same
 * anywhere, so the popover hangs from its top edge instead, where the section
 * starts.
 */
export function placePopover(
  rect: DOMRect,
  placement: GuidePlacement,
  popover: Size,
): PopoverPlacement {
  const candidates: GuidePlacement[] = [
    placement,
    OPPOSITE[placement],
    'bottom',
    'top',
  ];
  const side = candidates.find((candidate) => fits(candidate, rect, popover));
  if (side) return { anchor: rect, side };

  const viewport = window.innerHeight;
  const reachesBottom = rect.bottom >= viewport - POPOVER_COLLISION_PADDING;
  const coveredAtTop = overlap(
    rect,
    POPOVER_COLLISION_PADDING,
    POPOVER_COLLISION_PADDING + popover.height,
  );
  const coveredAtBottom = overlap(
    rect,
    viewport - POPOVER_COLLISION_PADDING - popover.height,
    viewport - POPOVER_COLLISION_PADDING,
  );
  if (!reachesBottom && coveredAtTop < coveredAtBottom) {
    // Radix hangs the popover a side offset above this zero-height anchor,
    // which leaves its top edge on the padding.
    const edge = popover.height + CLEARANCE;
    return { anchor: new DOMRect(rect.left, edge, rect.width, 0), side: 'top' };
  }

  // Lowest the edge can sit with the whole popover still above the padding.
  const lowestTop = viewport - popover.height - CLEARANCE;
  const top = reachesBottom ? Math.min(rect.top, lowestTop) : lowestTop;

  return { anchor: new DOMRect(rect.left, top, rect.width, 0), side: 'bottom' };
}

function overlap(rect: DOMRect, top: number, bottom: number) {
  return Math.max(0, Math.min(rect.bottom, bottom) - Math.max(rect.top, top));
}
